import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import AuthLayout from "@container/layout/auth";
import { Input } from "@component/input";
import { Card, CardContent } from "@component/card";
import { searchCandidatesAdmin } from "@service/admin";
import { AdminCandidateSearchItem } from "@type/admin";
import { catchErrorLog } from "@util/sentry";
import { parseFormat } from "@util/date";
import { LifeBuoyIcon, SearchIcon, UserIcon, MailIcon, PhoneIcon, BriefcaseIcon, AlertCircleIcon } from "lucide-react";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const MIN_QUERY_LEN = 3;
const DEBOUNCE_MS = 400;

function getInitials(name: string): string {
    if (!name) return "?";
    return name.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase();
}

function detectQueryKind(q: string): "uuid" | "email" | "prefix" {
    if (UUID_RE.test(q.trim())) return "uuid";
    if (q.includes("@")) return "email";
    return "prefix";
}

export default function AdminCandidateInvestigatorPage() {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState<AdminCandidateSearchItem[] | null>(null);
    const [isSearching, setIsSearching] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>("");

    const trimmed = query.trim();
    const queryKind = useMemo(() => detectQueryKind(trimmed), [trimmed]);

    useEffect(() => {
        if (trimmed.length < MIN_QUERY_LEN) {
            setResults(null);
            setErrorMessage("");
            setIsSearching(false);
            return;
        }

        const controller = new AbortController();
        const timer = setTimeout(async () => {
            setIsSearching(true);
            setErrorMessage("");
            try {
                const resp = await searchCandidatesAdmin(trimmed);
                if (controller.signal.aborted) return;
                setResults(resp.data?.data?.candidates ?? []);
            } catch (err) {
                if (axios.isCancel(err) || controller.signal.aborted) return;
                if (axios.isAxiosError(err)) {
                    const msg = (err.response?.data as { message?: string } | undefined)?.message
                        || "Failed to search candidates.";
                    setErrorMessage(msg);
                } else {
                    setErrorMessage("Failed to search candidates.");
                }
                catchErrorLog(`Admin candidate search failed: ${err}`);
                setResults([]);
            } finally {
                if (!controller.signal.aborted) setIsSearching(false);
            }
        }, DEBOUNCE_MS);

        return () => {
            controller.abort();
            clearTimeout(timer);
        };
    }, [trimmed]);

    return (
        <AuthLayout>
            <div className="space-y-6 max-w-3xl">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center">
                        <LifeBuoyIcon className="w-5 h-5" />
                    </div>
                    <div>
                        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Candidate Investigator</h1>
                        <p className="text-sm text-neutral-500">
                            Find a candidate by email or user ID to investigate screening issues.
                        </p>
                    </div>
                </div>

                <div className="space-y-2">
                    <div className="relative">
                        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                        <Input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search by email or user ID (UUID)…"
                            className="pl-9 h-11"
                            autoFocus
                        />
                    </div>
                    <div className="flex items-center justify-between text-xs text-neutral-500">
                        <span>
                            {trimmed.length < MIN_QUERY_LEN
                                ? `Type at least ${MIN_QUERY_LEN} characters to search.`
                                : `Searching by ${queryKind === "uuid" ? "user ID" : "email"}.`}
                        </span>
                        {isSearching && <span>Searching…</span>}
                    </div>
                </div>

                {errorMessage && (
                    <Card className="border-red-200 bg-red-50">
                        <CardContent className="p-4 flex items-start gap-3">
                            <AlertCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-red-700">{errorMessage}</div>
                        </CardContent>
                    </Card>
                )}

                {results && !errorMessage && (
                    <div className="space-y-2">
                        {results.length === 0 ? (
                            <Card>
                                <CardContent className="p-8 text-center text-sm text-neutral-500">
                                    No candidate matched <span className="font-medium text-gray-900">"{trimmed}"</span>.
                                </CardContent>
                            </Card>
                        ) : (
                            <>
                                <p className="text-xs text-neutral-500">
                                    {results.length} candidate{results.length === 1 ? "" : "s"} found
                                </p>
                                <div className="space-y-2">
                                    {results.map((c) => (
                                        <Link
                                            key={c.id}
                                            to={`/candidate-investigator/${c.id}`}
                                            state={{ name: c.name, email: c.email, phone: c.phone, country_code: c.country_code }}
                                            className="block"
                                        >
                                            <Card className="hover:shadow-md hover:border-neutral-300 transition-shadow">
                                                <CardContent className="p-4 flex items-center gap-4">
                                                    <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-semibold flex-shrink-0">
                                                        {getInitials(c.name)}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <p className="text-sm font-semibold text-gray-900 truncate">
                                                                {c.name || "(no name)"}
                                                            </p>
                                                            <span className="inline-flex items-center gap-1 text-xs font-medium text-neutral-500 bg-neutral-100 px-2 py-0.5 rounded-full">
                                                                <BriefcaseIcon className="w-3 h-3" />
                                                                {c.total_applications}
                                                            </span>
                                                        </div>
                                                        <div className="mt-1 flex items-center flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-600">
                                                            <span className="inline-flex items-center gap-1">
                                                                <MailIcon className="w-3 h-3" />
                                                                {c.email}
                                                            </span>
                                                            {c.phone && (
                                                                <span className="inline-flex items-center gap-1">
                                                                    <PhoneIcon className="w-3 h-3" />
                                                                    {c.country_code ? `+${c.country_code.replace(/^\+/, "")} ` : ""}
                                                                    {c.phone}
                                                                </span>
                                                            )}
                                                            <span className="inline-flex items-center gap-1 text-neutral-400">
                                                                <UserIcon className="w-3 h-3" />
                                                                <span className="font-mono">{c.id.slice(0, 8)}…</span>
                                                            </span>
                                                        </div>
                                                        {c.last_active_at && (
                                                            <p className="mt-1 text-[11px] text-neutral-400">
                                                                Last active {parseFormat(c.last_active_at, "D MMM YYYY HH:mm")}
                                                            </p>
                                                        )}
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        </Link>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
        </AuthLayout>
    );
}
